// Enhanced Service Worker Registration with comprehensive PWA features
// Includes update notifications, offline detection, and better error handling

const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
    window.location.hostname === '[::1]' ||
    window.location.hostname.match(/^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/)
);

// Service Worker Update Manager
class ServiceWorkerUpdateManager {
  constructor() {
    this.registration = null;
    this.isUpdateAvailable = false;
    this.callbacks = {
      onUpdate: null,
      onSuccess: null,
      onOffline: null,
      onOnline: null
    };
    
    this.setupOnlineOfflineHandlers();
  }

  setupOnlineOfflineHandlers() {
    window.addEventListener('online', () => {
      console.log('App is back online');
      if (this.callbacks.onOnline) {
        this.callbacks.onOnline();
      }
      this.showNotification('Connection restored', 'You are back online!', 'success');
    });

    window.addEventListener('offline', () => {
      console.log('App is offline');
      if (this.callbacks.onOffline) {
        this.callbacks.onOffline();
      }
      this.showNotification('Offline mode', 'App will work offline with cached content', 'info');
    });
  }

  showNotification(title, message, type = 'info') {
    // Create a simple notification system
    const notification = document.createElement('div');
    notification.className = `sw-notification sw-notification-${type}`;
    notification.innerHTML = `
      <div class="sw-notification-content">
        <strong>${title}</strong>
        <p>${message}</p>
        <button onclick="this.parentElement.parentElement.remove()">×</button>
      </div>
    `;
    
    // Add styles if not already present
    if (!document.getElementById('sw-notification-styles')) {
      const styles = document.createElement('style');
      styles.id = 'sw-notification-styles';
      styles.textContent = `
        .sw-notification {
          position: fixed;
          top: 20px;
          right: 20px;
          max-width: 300px;
          padding: 15px;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
          z-index: 10000;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          animation: slideIn 0.3s ease-out;
        }
        .sw-notification-success { background: #d4edda; border-left: 4px solid #28a745; color: #155724; }
        .sw-notification-info { background: #d1ecf1; border-left: 4px solid #17a2b8; color: #0c5460; }
        .sw-notification-warning { background: #fff3cd; border-left: 4px solid #ffc107; color: #856404; }
        .sw-notification-error { background: #f8d7da; border-left: 4px solid #dc3545; color: #721c24; }
        .sw-notification-content { position: relative; }
        .sw-notification button {
          position: absolute;
          top: -5px;
          right: -5px;
          background: none;
          border: none;
          font-size: 18px;
          cursor: pointer;
          opacity: 0.7;
        }
        .sw-notification button:hover { opacity: 1; }
        @keyframes slideIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `;
      document.head.appendChild(styles);
    }
    
    document.body.appendChild(notification);
    
    // Auto remove after 5 seconds
    setTimeout(() => {
      if (notification.parentElement) {
        notification.remove();
      }
    }, 5000);
  }

  showUpdateAvailablePrompt() {
    const updatePrompt = document.createElement('div');
    updatePrompt.className = 'sw-update-prompt';
    updatePrompt.innerHTML = `
      <div class="sw-update-content">
        <h3>Update Available</h3>
        <p>A new version of the app is available. Would you like to update now?</p>
        <div class="sw-update-buttons">
          <button id="sw-update-now" class="sw-btn-primary">Update Now</button>
          <button id="sw-update-later" class="sw-btn-secondary">Later</button>
        </div>
      </div>
    `;

    // Add update prompt styles
    if (!document.getElementById('sw-update-styles')) {
      const styles = document.createElement('style');
      styles.id = 'sw-update-styles';
      styles.textContent = `
        .sw-update-prompt {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10001;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }
        .sw-update-content {
          background: white;
          padding: 30px;
          border-radius: 12px;
          max-width: 400px;
          text-align: center;
          box-shadow: 0 8px 32px rgba(0,0,0,0.3);
        }
        .sw-update-content h3 {
          margin: 0 0 15px 0;
          color: #333;
        }
        .sw-update-content p {
          margin: 0 0 25px 0;
          color: #666;
          line-height: 1.5;
        }
        .sw-update-buttons {
          display: flex;
          gap: 10px;
          justify-content: center;
        }
        .sw-btn-primary, .sw-btn-secondary {
          padding: 10px 20px;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
          transition: all 0.2s;
        }
        .sw-btn-primary {
          background: #007bff;
          color: white;
        }
        .sw-btn-primary:hover {
          background: #0056b3;
        }
        .sw-btn-secondary {
          background: #6c757d;
          color: white;
        }
        .sw-btn-secondary:hover {
          background: #545b62;
        }
      `;
      document.head.appendChild(styles);
    }

    document.body.appendChild(updatePrompt);

    // Handle update buttons
    document.getElementById('sw-update-now').onclick = () => {
      this.applyUpdate();
      updatePrompt.remove();
    };

    document.getElementById('sw-update-later').onclick = () => {
      updatePrompt.remove();
    };
  }

  applyUpdate() {
    if (this.registration && this.registration.waiting) {
      // Signal that this is a user-initiated update so the page reload is expected
      if (this._setUserInitiatedUpdate) {
        this._setUserInitiatedUpdate();
      }
      this.registration.waiting.postMessage({ type: 'SKIP_WAITING' });
    }
  }

  // Communication with service worker
  sendMessage(message) {
    return new Promise((resolve, reject) => {
      const messageChannel = new MessageChannel();
      messageChannel.port1.onmessage = (event) => {
        if (event.data.error) {
          reject(event.data.error);
        } else {
          resolve(event.data);
        }
      };

      if (this.registration && this.registration.active) {
        this.registration.active.postMessage(message, [messageChannel.port2]);
      } else {
        reject(new Error('Service worker not active'));
      }
    });
  }

  // Cache management methods
  async cacheUrls(urls) {
    try {
      await this.sendMessage({ type: 'CACHE_URLS', payload: { urls } });
      console.log('URLs cached successfully');
    } catch (error) {
      console.error('Failed to cache URLs:', error);
    }
  }

  async clearCache(cacheName) {
    try {
      await this.sendMessage({ type: 'CLEAR_CACHE', payload: { cacheName } });
      console.log('Cache cleared successfully');
    } catch (error) {
      console.error('Failed to clear cache:', error);
    }
  }

  async getCachedUrls() {
    try {
      const result = await this.sendMessage({ type: 'GET_CACHED_URLS' });
      return result.urls;
    } catch (error) {
      console.error('Failed to get cached URLs:', error);
      return [];
    }
  }
}

// Global instance
const swUpdateManager = new ServiceWorkerUpdateManager();

export function register(config) {
  if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
    const publicUrl = new URL(process.env.PUBLIC_URL, window.location.href);
    if (publicUrl.origin !== window.location.origin) {
      return;
    }

    window.addEventListener('load', () => {
      const swUrl = `${process.env.PUBLIC_URL}/service-worker.js`;

      if (isLocalhost) {
        checkValidServiceWorker(swUrl, config);
        navigator.serviceWorker.ready.then(() => {
          console.log('This web app is being served cache-first by a service worker.');
        });
      } else {
        registerValidSW(swUrl, config);
      }
    });
  }
}

function registerValidSW(swUrl, config) {
  navigator.serviceWorker
    .register(swUrl)
    .then((registration) => {
      swUpdateManager.registration = registration;

      // Handle service worker updates
      registration.addEventListener('updatefound', () => {
        const installingWorker = registration.installing;
        if (installingWorker == null) return;

        installingWorker.addEventListener('statechange', () => {
          if (installingWorker.state === 'installed') {
            if (navigator.serviceWorker.controller) {
              // New content available
              console.log('New content is available and will be used when all tabs are closed.');
              swUpdateManager.isUpdateAvailable = true;
              swUpdateManager.showUpdateAvailablePrompt();
              
              if (config && config.onUpdate) {
                config.onUpdate(registration);
              }
            } else {
              // Content cached for first time
              console.log('Content is cached for offline use.');
              swUpdateManager.showNotification(
                'App Ready', 
                'App is now available offline!', 
                'success'
              );
              
              if (config && config.onSuccess) {
                config.onSuccess(registration);
              }
            }
          }
        });
      });

      // Listen for controlling service worker changes
      // Only reload if the user explicitly accepted the update (clicked "Update Now")
      let isUserInitiatedUpdate = false;
      
      // Expose a way for the update manager to signal user-initiated updates
      swUpdateManager._setUserInitiatedUpdate = () => {
        isUserInitiatedUpdate = true;
      };

      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (isUserInitiatedUpdate) {
          window.location.reload();
        } else {
          // New service worker took over automatically (e.g., after closing all tabs)
          // Don't force reload — let the user continue their session
          console.log('Service worker updated in background. Changes will apply on next navigation.');
        }
      });

      // Setup periodic sync if supported
      if ('serviceWorker' in navigator && 'sync' in window.ServiceWorkerRegistration.prototype) {
        registration.sync.register('background-sync');
      }

      // Setup periodic background sync if supported
      if ('periodicSync' in registration) {
        registration.periodicSync.register('content-sync', {
          minInterval: 24 * 60 * 60 * 1000, // 24 hours
        });
      }

    })
    .catch((error) => {
      console.error('Error during service worker registration:', error);
      swUpdateManager.showNotification(
        'Registration Failed', 
        'Service worker registration failed', 
        'error'
      );
    });
}

function checkValidServiceWorker(swUrl, config) {
  fetch(swUrl, {
    headers: { 'Service-Worker': 'script' },
  })
    .then((response) => {
      const contentType = response.headers.get('content-type');
      if (
        response.status === 404 ||
        (contentType != null && contentType.indexOf('javascript') === -1)
      ) {
        navigator.serviceWorker.ready.then((registration) => {
          registration.unregister().then(() => {
            window.location.reload();
          });
        });
      } else {
        registerValidSW(swUrl, config);
      }
    })
    .catch(() => {
      console.log('No internet connection found. App is running in offline mode.');
      swUpdateManager.showNotification(
        'Offline Mode', 
        'No internet connection. Running in offline mode.', 
        'warning'
      );
    });
}

export function unregister() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => {
        registration.unregister();
      })
      .catch((error) => {
        console.error(error.message);
      });
  }
}

// Export the update manager for use in components
export { swUpdateManager };

// Push notification helpers
export function requestNotificationPermission() {
  if ('Notification' in window && 'serviceWorker' in navigator) {
    return Notification.requestPermission().then((permission) => {
      if (permission === 'granted') {
        console.log('Notification permission granted');
        return true;
      } else {
        console.log('Notification permission denied');
        return false;
      }
    });
  }
  return Promise.resolve(false);
}

export function subscribeToPushNotifications() {
  if ('serviceWorker' in navigator && 'PushManager' in window) {
    return navigator.serviceWorker.ready.then((registration) => {
      return registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(process.env.REACT_APP_VAPID_PUBLIC_KEY || '')
      });
    });
  }
  return Promise.resolve(null);
}

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}
